import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import { CButton, CCollapse } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import { SCS_GROUPING_TABLE_OPTIONS } from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_PMD_MARKETED_OPTIONS,
  SCS_PMD_NOT_MARKETED_TEMPLATE,
  SCS_PMD_QUESTIONS,
} from "../../utils/scsPostMarketingDataQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-post-marketing-data-form";
const REMOVE_TOOLTIP_ID = "scs-pmd-remove";
const DUPLICATE_TOOLTIP_ID = "scs-pmd-duplicate";

// The repeatable table/analysis sections always keep at least one entry; the
// first entry is non-removable (mirrors the Analysis of Adverse Events /
// Description of Studies pattern).
const MIN_BLOCK_ENTRIES = 1;

// UI shape of one table/analysis section (the whole Q1–Q2 flow). This page is
// organised around these sections only — Q1 answered "No" ends the section, so
// no table question is shown and none is saved.
const emptyAnalysis = () => ({
  qPostMarketing: null,
  qPostMarketingTable: "",
});

// Q1 is saved as a boolean; tolerate the "Yes" / "No" wording as well so an
// answer saved from the button labels still restores (same tolerance as the
// Subject Disposition Q1).
const marketedFrom = value => {
  if (typeof value === "boolean") return value;
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

// Deep-clone a table/analysis section so a duplicated block is fully
// independent from its source (mutating one never affects the other). Also used
// to rebuild a block from saved data, where the table is absent whenever Q1 was
// answered "No".
const cloneAnalysis = analysis => ({
  ...emptyAnalysis(),
  qPostMarketing: marketedFrom(analysis?.qPostMarketing),
  qPostMarketingTable: analysis?.qPostMarketingTable || "",
});

// The saved answers arrive either bare or wrapped, and as a list of sections or
// as a single section; collapse every shape to a plain list of sections.
const savedAnalysesFrom = payload => {
  const section = payload?.post_marketing_data || payload;
  if (!section || typeof section !== "object") return [];
  if (Array.isArray(section)) return section.filter(Boolean);
  const analyses = section.analyses;
  if (Array.isArray(analyses)) return analyses.filter(Boolean);
  if (analyses && typeof analyses === "object") return [analyses];
  return [];
};

const buildAnalyses = saved => {
  const blocks = (saved || []).map(cloneAnalysis);
  return blocks.length > 0 ? blocks : [emptyAnalysis()];
};

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));

// Render a single-select's chosen value as a chip/tag so it visually matches
// the multi-select dropdowns of the other SCS wizard pages — look only, the
// control stays single-select (copied from the Analysis of Adverse Events
// page).
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

const SCSPostMarketingData = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();

  // The Q2 table dropdown is populated from the safety-summary TFL content in
  // the scsData store once the uploaded files have been PROCESSED; until then
  // fall back to the bundled placeholders.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const tableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;

  const [questionText, setQuestionText] = useState({});
  // One entry per table/analysis section — the only repeatable unit this page
  // is organised around.
  const [analyses, setAnalyses] = useState([emptyAnalysis()]);
  // Which table/analysis accordion items are open (by array index). The first
  // is expanded when the page loads; a newly added / duplicated section (always
  // the last) becomes the only expanded one.
  const [openAnalysisIndexes, setOpenAnalysisIndexes] = useState(
    () => new Set([0])
  );

  // Q1 is required per table/analysis section: { [analysisIndex]: message }.
  const [analysisErrors, setAnalysisErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Table/analysis blocks, so a newly added one — or the one holding the first
  // validation error — can be scrolled into view.
  const analysisRefs = useRef({});
  const pendingScrollRef = useRef(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state. Only the first section is left
  // expanded once the saved answers are restored.
  useEffect(() => {
    if (!summaryId) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryPostMarketingData({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        setAnalyses(buildAnalyses(savedAnalysesFrom(data)));
        setOpenAnalysisIndexes(new Set([0]));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading]);

  // Scroll a newly added / duplicated table/analysis section — or the one
  // holding the first validation error — into view. Walk up to the named scroll
  // container so we never scroll the browser viewport (matches the Analysis of
  // Adverse Events block behaviour).
  useEffect(() => {
    const index = pendingScrollRef.current;
    if (index === null || index === undefined) return;
    pendingScrollRef.current = null;
    const el = analysisRefs.current[index];
    if (!el) return;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
  });

  // --- Accordion open/closed state ----------------------------------------
  const toggleAnalysis = index =>
    setOpenAnalysisIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  // --- Table/analysis mutators --------------------------------------------
  const updateAnalysis = (analysisIndex, updater) =>
    setAnalyses(prev =>
      prev.map((row, i) => (i === analysisIndex ? updater(row) : row))
    );

  // "Add another table/analysis" appends an empty section, "Duplicate another
  // table/analysis" appends a copy of the last one and the per-section
  // "Duplicate" pill appends a copy of that section. New sections always go to
  // the end and become the only expanded accordion item.
  const appendAnalysis = factory => {
    const added = factory(analyses);
    if (!added) return;
    setAnalyses(prev => [...prev, added]);
    pendingScrollRef.current = analyses.length;
    setOpenAnalysisIndexes(new Set([analyses.length]));
  };

  const addAnalysis = () => appendAnalysis(() => emptyAnalysis());
  const duplicateAnalysis = analysisIndex =>
    appendAnalysis(list =>
      list[analysisIndex] ? cloneAnalysis(list[analysisIndex]) : null
    );
  const duplicateAnalysisAbove = () =>
    appendAnalysis(list =>
      list.length ? cloneAnalysis(list[list.length - 1]) : null
    );
  const removeAnalysis = analysisIndex => {
    if (analyses.length <= MIN_BLOCK_ENTRIES) return;
    setAnalyses(prev => prev.filter((_, i) => i !== analysisIndex));
    // The remaining sections shift down a place, so drop the stale open indexes
    // and leave the last remaining section expanded.
    setOpenAnalysisIndexes(new Set([analyses.length - 2]));
    clearError(analysisIndex);
  };

  const clearError = analysisIndex =>
    setAnalysisErrors(prev => {
      if (prev[analysisIndex] === undefined) return prev;
      const next = { ...prev };
      delete next[analysisIndex];
      return next;
    });

  // Q1 — "No" ends the section, so the table picked before the answer was
  // switched is dropped with it (the standard wording is reported instead).
  const setMarketed = (analysisIndex, value) => {
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      qPostMarketing: value,
      qPostMarketingTable: value ? analysis.qPostMarketingTable : "",
    }));
    clearError(analysisIndex);
  };

  const setPostMarketingTable = (analysisIndex, value) =>
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      qPostMarketingTable: value,
    }));

  // Every table/analysis section needs its Q1 answer before the page can be
  // left — it decides whether the section reports a table or the standard "not
  // marketed" wording.
  const validate = () => {
    const errors = {};
    analyses.forEach((analysis, index) => {
      if (typeof analysis.qPostMarketing === "boolean") return;
      errors[index] = "Please select an answer";
    });
    setAnalysisErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const index = Number(firstInvalid);
      // Expand the offending section before scrolling to it, otherwise the
      // question is hidden inside a collapsed accordion item.
      setOpenAnalysisIndexes(prev => new Set(prev).add(index));
      pendingScrollRef.current = index;
      return false;
    }
    return true;
  };

  const buildAnalysisPayload = analysis => {
    const marketed = marketedFrom(analysis.qPostMarketing);
    // Unanswered — validation keeps this from being saved, but stay defensive.
    if (marketed === null) return { qPostMarketing: null };
    // "No" ends the section: no table is reported and the standard wording is
    // what gets inserted instead.
    if (!marketed) {
      return {
        qPostMarketing: false,
        qPostMarketingTable: null,
        qPostMarketingText: SCS_PMD_NOT_MARKETED_TEMPLATE,
      };
    }
    return {
      qPostMarketing: true,
      qPostMarketingTable: (analysis.qPostMarketingTable || "").trim() || null,
    };
  };

  const buildPayload = () => ({
    analyses: analyses.map(buildAnalysisPayload),
  });

  // Persists the wizard state via PATCH .../wizards/post_marketing. Shared by
  // the "Save" button (stays on the page) and the "Data Mapping" button
  // (navigates on success); returns whether the save succeeded so the caller
  // can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummaryPostMarketingData({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/data-mapping`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each table/analysis section.
  const duplicateBlockButton = (label, onClick) => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      aria-label={label}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with the matching "Add another …" control.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question text.
  const qText = key => {
    const question = SCS_PMD_QUESTIONS[key];
    return (
      questionText[key] ||
      questionText[question?.textKey] ||
      question?.text ||
      ""
    );
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_PMD_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — Yes / No toggle. Selecting "No" ends the section.
  const renderMarketed = (analysis, a, number) => {
    const error = analysisErrors[a];
    return renderQuestion(
      "qPostMarketing",
      <>
        {SCS_PMD_MARKETED_OPTIONS.map(option => (
          <React.Fragment key={String(option.value)}>
            <input
              type={"radio"}
              className={"btn-check"}
              name={`post-marketing-${a}`}
              id={`post-marketing-${a}-${option.label}`}
              autoComplete={"off"}
              checked={analysis.qPostMarketing === option.value}
              onChange={() => setMarketed(a, option.value)}
            />
            <label
              className={"btn btn-light"}
              htmlFor={`post-marketing-${a}-${option.label}`}>
              {option.label}
            </label>
          </React.Fragment>
        ))}
        {error && <span className={"errmsg"}>{error}</span>}
      </>,
      number
    );
  };

  // Q2 — single-select of the SCS TFL tables. The table is reproduced as-is
  // (country, product name, approximate doses/patients exposed), so there is
  // nothing else to ask for.
  const renderPostMarketingTable = (analysis, a, number) =>
    renderQuestion(
      "qPostMarketingTable",
      <Select
        className={"scs-dos__control"}
        inputId={`post-marketing-table-${a}`}
        styles={SINGLE_VALUE_CHIP_STYLES}
        options={toOptions(tableOptions)}
        value={
          analysis.qPostMarketingTable
            ? {
                value: analysis.qPostMarketingTable,
                label: analysis.qPostMarketingTable,
              }
            : null
        }
        onChange={selected =>
          setPostMarketingTable(a, selected ? selected.value : "")
        }
        placeholder={"Select post-marketing data table"}
        isClearable
      />,
      number
    );

  // Answering "No" to Q1 reports this standard wording instead of a table. It
  // is shown read-only — the "[drug name]" placeholders are filled in when the
  // section is generated.
  const renderNotMarketedTemplate = () => (
    <div className={"questions-content__container"}>
      <div className={"questions-content__questionIndexContainer"}>
        <span className={"empty-space-layout-helper"}></span>
      </div>
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            The following standard text will be inserted in this section:
          </span>
        </div>
        <div className={"questions-content__input"}>
          <p className={"scs-pmd__template"}>{SCS_PMD_NOT_MARKETED_TEMPLATE}</p>
        </div>
      </div>
    </div>
  );

  // One table/analysis section: Q1 and, when the drug is marketed, Q2. "No"
  // ends the section, so the standard wording replaces everything below it.
  const renderAnalysisBlock = (analysis, a) => (
    <>
      <div className={"scs-dos__block-actions"}>
        {duplicateBlockButton("Duplicate this table/analysis", () =>
          duplicateAnalysis(a)
        )}
        {analyses.length > MIN_BLOCK_ENTRIES &&
          removeButton("Remove table/analysis", () => removeAnalysis(a))}
      </div>
      {renderMarketed(analysis, a, 1)}
      {analysis.qPostMarketing === true &&
        renderPostMarketingTable(analysis, a, 2)}
      {analysis.qPostMarketing === false && renderNotMarketedTemplate()}
    </>
  );

  // The table/analysis sections are what this page repeats: a single section is
  // rendered on its own, more than one is wrapped in an accordion (the first
  // one expanded on load).
  const renderAnalyses = () => (
    <>
      {analyses.length > 1 && (
        <div className={"scs-dos__accordion-controls"}>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() =>
              setOpenAnalysisIndexes(new Set(analyses.map((_, i) => i)))
            }>
            Expand all
          </CButton>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() => setOpenAnalysisIndexes(new Set())}>
            Collapse all
          </CButton>
        </div>
      )}
      {analyses.length === 1 ? (
        <div
          ref={el => (analysisRefs.current[0] = el)}
          className={"analysis-block"}>
          {renderAnalysisBlock(analyses[0], 0)}
        </div>
      ) : (
        <div className={"accordion scs-pmd__analyses"}>
          {analyses.map((analysis, a) => {
            const isOpen = openAnalysisIndexes.has(a);
            return (
              <div
                key={`analysis-${a}`}
                className={"accordion-item"}
                ref={el => (analysisRefs.current[a] = el)}>
                <div className={"accordion-header"}>
                  <button
                    type={"button"}
                    className={`accordion-button${isOpen ? "" : " collapsed"}`}
                    onClick={() => toggleAnalysis(a)}>
                    {analysis.qPostMarketingTable || `Table/Analysis ${a + 1}`}
                  </button>
                </div>
                <CCollapse visible={isOpen} className={"accordion-collapse"}>
                  <div className={"accordion-body"}>
                    <div className={"analysis-block"}>
                      {renderAnalysisBlock(analysis, a)}
                    </div>
                  </div>
                </CCollapse>
              </div>
            );
          })}
        </div>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another table/analysis", addAnalysis)}
        {duplicateAboveButton(
          "Duplicate another table/analysis",
          duplicateAnalysisAbove
        )}
      </div>
    </>
  );

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos scs-pmd"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {renderAnalyses()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Post-Marketing Data"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Safety in Special Groups",
        buttonGoToAction: `/scs/${summaryId}/safety-in-special-groups`,
      }}
      saveButton={true}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Data Mapping",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/data-mapping`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSPostMarketingData;
